/**
 * useIndustryProjects Hook
 * Generic CRUD operations for industry_projects table
 * Automatically filters by domain (CompanyType)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type {
  IndustryProject,
  CreateProjectInput,
  UpdateProjectInput,
  ProjectStatus,
} from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';

interface UseIndustryProjectsOptions {
  domain: CompanyType;
  status?: ProjectStatus | ProjectStatus[];
  client_id?: number;
}

export function useIndustryProjects(options: UseIndustryProjectsOptions) {
  const { domain, status, client_id } = options;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get current company (assumes user is authenticated and has company)
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch projects
  const {
    data: projects,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['industry_projects', company?.id, domain, status, client_id],
    queryFn: async () => {
      if (!company) return [];

      let query = supabase
        .from('industry_projects')
        .select('*')
        .eq('company_id', company.id)
        .eq('domain', domain)
        .order('created_at', { ascending: false });

      // Optional filters
      if (status) {
        if (Array.isArray(status)) {
          query = query.in('status', status);
        } else {
          query = query.eq('status', status);
        }
      }

      if (client_id) {
        query = query.eq('client_id', client_id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as IndustryProject[];
    },
    enabled: !!company,
  });

  // Create project
  const createProject = useMutation({
    mutationFn: async (input: CreateProjectInput) => {
      if (!company) throw new Error('No company found');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('industry_projects')
        .insert({
          ...input,
          domain,
          company_id: company.id,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as IndustryProject;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_projects', company?.id, domain],
      });
      toast({
        title: 'Projeto criado',
        description: `${data.name} foi criado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar projeto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update project
  const updateProject = useMutation({
    mutationFn: async (input: UpdateProjectInput) => {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('industry_projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as IndustryProject;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_projects', company?.id, domain],
      });
      toast({
        title: 'Projeto atualizado',
        description: `${data.name} foi atualizado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar projeto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete project
  const deleteProject = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('industry_projects')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industry_projects', company?.id, domain],
      });
      toast({
        title: 'Projeto excluído',
        description: 'O projeto foi excluído com sucesso.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir projeto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    projects: projects || [],
    isLoading,
    error,
    refetch,
    createProject: createProject.mutate,
    createProjectAsync: createProject.mutateAsync,
    updateProject: updateProject.mutate,
    updateProjectAsync: updateProject.mutateAsync,
    deleteProject: deleteProject.mutate,
    deleteProjectAsync: deleteProject.mutateAsync,
    isCreating: createProject.isPending,
    isUpdating: updateProject.isPending,
    isDeleting: deleteProject.isPending,
  };
}
